import React, {
    useContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ModalNextContext,
    Loader,
    FieldValue,
    CurrencyValue,
    InlineNotification,
} from '@jutro/components';
import {
    Currency,
    LoaderExt,
    PaymentComponent,
} from 'gw-components-platform-react';
import { IntlContext, Grid, useTranslator } from '@jutro/locale';
import { MetadataForm } from '@jutro/legacy/uiconfig';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { getNormalizedLOBName } from '@xengage/gw-portals-config-js';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import moment from 'moment';
import { useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import RecentAccountPaymentTable from '../RecentAccountPaymentTable/RecentAccountPaymentTable';
import BillingDocumentsTable from '../BillingDocumentsTable/BillingDocumentsTable';
import metadata from './BillingSummary.metadata.json5';
import styles from './BillingSummary.module.scss';
import messages from './BillingSummary.messages';
import usecpmiAnalytics from '../../../hooks/usecpmiAnalytics';
import useGoogleAnalytics from '../../../hooks/useGoogleAnalytics';

const policyLevelHeaderStrings = {
    pa: {
        autoPaymentEnabled: messages.personalAutoPolicyAutomaticPaymentsEnabled,
        default: messages.personalAutoPolicy,
    },
    ho: {
        autoPaymentEnabled: messages.homeownersPolicyAutomaticPaymentsEnabled,
        default: messages.homeownersPolicy,
    },
    bop: {
        autoPaymentEnabled:
            messages.businessOwnersPolicyAutomaticPaymentsEnabled,
        default: messages.businessOwnersPolicy,
    },
    ca: {
        autoPaymentEnabled:
            messages.commercialAutoPolicyAutomaticPaymentsEnabled,
        default: messages.commercialAutoPolicy,
    },
    cp: {
        autoPaymentEnabled:
            messages.commercialPropertyPolicyAutomaticPaymentsEnabled,
        default: messages.commercialPropertyPolicy,
    },
    cpkg: {
        autoPaymentEnabled:
            messages.commercialPackagePolicyAutomaticPaymentsEnabled,
        default: messages.commercialPackagePolicy,
    },
    gl: {
        autoPaymentEnabled:
            messages.generalLiabilityPolicyAutomaticPaymentsEnabled,
        default: messages.generalLiabilityPolicy,
    },
    im: {
        autoPaymentEnabled: messages.inlandMarinePolicyAutomaticPaymentsEnabled,
        default: messages.inlandMarinePolicy,
    },
    wc: {
        autoPaymentEnabled: messages.workersCompPolicyAutomaticPaymentsEnabled,
        default: messages.workersCompPolicy,
    },
    wc7: {
        autoPaymentEnabled: messages.workersComp7PolicyAutomaticPaymentsEnabled,
        default: messages.workersComp7Policy,
    },
    default: {
        autoPaymentEnabled: messages.policyAutomaticPaymentsEnabled,
        default: messages.policy,
    },
};

const getAnyPaidInvoices = invoiceSummary => {
    return _.filter(invoiceSummary, item => {
        const { amount } = item.totalPaid;
        return item.paidStatus === 'fullypaid' || amount !== 0;
    });
};

const getUnpaidInvoices = invoiceSummary => {
    return _.filter(invoiceSummary, item => item.paidStatus !== 'fullypaid');
};

const hasUnpaidInvoices = invoiceSummary => {
    const unpaidInvoices = getUnpaidInvoices(invoiceSummary);
    return unpaidInvoices.length > 0;
};

const createPaymentInstrumentObject = (paymentDetailsVm, paymentMethod) => {
    const paymentDetailsValue = paymentDetailsVm.value;

    const bankAccountData = {
        ...paymentDetailsValue,
    };

    const creditCardData = {
        ...paymentDetailsValue,
    };

    let detailsValue;
    if (paymentMethod === 'wire') {
        detailsValue = { bankAccountData };
    } else {
        detailsValue = { creditCardData };
    }

    const paymentInstrumentValue = {
        paymentMethod,
        ...detailsValue,
    };

    return paymentInstrumentValue;
};

const getPolicyLevelInvoiceStreamHeader = (lobCode, isAutoPaymentEnabled) => {
    const normalizedLOBName = getNormalizedLOBName(lobCode);
    const lobHeader = _.has(policyLevelHeaderStrings, normalizedLOBName)
        ? policyLevelHeaderStrings[normalizedLOBName]
        : policyLevelHeaderStrings.default;

    if (isAutoPaymentEnabled) {
        return lobHeader.autoPaymentEnabled;
    }

    return lobHeader.default;
};

const getInvoiceStreamHeader = (
    isAccLevelStream,
    paymentGroupDetails,
    lobCode
) => {
    let header = '';
    const paymentInstrumentSummary = paymentGroupDetails
        ? paymentGroupDetails.paymentInstrumentSummary
        : null;
    const isAutomaticPaymentEnabled = paymentInstrumentSummary != null;
    if (isAccLevelStream) {
        header = isAutomaticPaymentEnabled
            ? messages.invoicesAutomaticPaymentsEnabled
            : messages.invoices;
    } else {
        header = getPolicyLevelInvoiceStreamHeader(
            lobCode,
            isAutomaticPaymentEnabled
        );
    }
    return header;
};

function BillingSummary(props) {
    const {
        invoiceStreamId,
        accountLevelBilling,
        lobCode,
        paymentGroupDetails,
        currency,
        policySummaries,
        authHeader,
        reloadInvoiceDetails,
        accountNumber,
        multiInvoceStreamDueAmount,
        multiInvoiceStreamData,
        multiInvoiceSelectedValue,
        multiInvoceStreamCurrentDueArray,
        BillingData,
        accountsData
    } = props;
    
    const intl = useContext(IntlContext);
    const translator = useTranslator();
    const { showAlert } = useContext(ModalNextContext);

    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );
    const { refreshData } = useDataRefresh();
    // screen states
    const [cancelLink, setCancelLink] = useState(false);
    const [automaticLink, setAutomaticLink] = useState(true);
    const [isLoadingData, updateIsLoadingData] = useState(false);
    const [
        selectedInvoicesForPayment,
        updateSelectedInvoicesForPayment,
    ] = useState([]);
    const [selectedInvoiceFilter, updateSelectedInvoiceFilter] = useState(
        'planned'
    );
    const [selectedScreen, updateSelectedScreen] = useState('default');
    const [manualAmountToPay, updateManualAmount] = useState(null);
    const [selectedInvoiceAmount, updateSelectedInvoiceAmount] = useState(0);
    const [tabValue, setTabValue] = useState(true);
    // const [invoiceData, setInvoiceData] = useState(invoiceSummary);
    const [currentDueAmount, setCurrentDueAmount] = useState([]);
    const [trackPageView, trackEvent] = usecpmiAnalytics();
    const [trackGAPageView] = useGoogleAnalytics();
    const [invoiceSummary, setInvoiceSummary] = useState([]);
    const [automaticPaymentsAcctNumber, setAutomaticPaymentsAcctNumber] = useState();
    

    useEffect(() => {
        trackGAPageView("/billing-summary", "Billing Summary")
        window.sessionStorage.removeItem('make-payment');
    },[])

    useEffect(()=>{
        const allMultiStreamSummaries = [];

        // eslint-disable-next-line array-callback-return
        BillingData?.allInvoiceStreamDetails.map((loopItem)=>{
            if(loopItem.internalId === multiInvoiceStreamData){
                loopItem.invoiceSummary.map((subLoopItem)=>{
                    allMultiStreamSummaries.push(subLoopItem);
                }) 
            }

        }) 
        setInvoiceSummary(allMultiStreamSummaries);
        

    },[BillingData?.allInvoiceStreamDetails])
    
    const payingAmountValue = manualAmountToPay ? manualAmountToPay.amount : 0;

    const {
        // invoiceSummary = [],
        relatedPolicies = [],
        paymentInstrument,
        periodicity,
    } = paymentGroupDetails.invoiceStreamDetails;
    
    const { recentAccountPayments = [] } = paymentGroupDetails;
    const { billingDocuments = [] } = paymentGroupDetails;

    const [selectedyear, setselectedyear] = useState(null);
    const [selectedDocYear, setSelectedDocYear] = useState(null);

    const firstInvoiceDueDate = useMemo(() => {
        const unPaidInvoices = _.filter(invoiceSummary, invoice => {
            return invoice.paidStatus !== 'paid';
        });

        const firstUnpaidInvoice = _.minBy(unPaidInvoices, invoice => {
            return new Date(invoice.dueDate).getTime();
        });

        return firstUnpaidInvoice ? firstUnpaidInvoice.dueDate : null;
    }, [invoiceSummary]);

    const resetInternalStates = useCallback(() => {
        updateIsLoadingData(true);
        reloadInvoiceDetails(invoiceStreamId, true).finally(() => {
            updateSelectedInvoicesForPayment([]);
            updateSelectedInvoiceFilter('planned');
            updateManualAmount(null);
            updateIsLoadingData(false);
        });
    }, [reloadInvoiceDetails, invoiceStreamId]);

    const getCurrentDueAmount = amount => {
        return amount.amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    };

    const currentDueDataMultiInvoiceStream = useCallback(() => {
        const dueData = multiInvoceStreamCurrentDueArray;
        // if (dueData.length > 4) {
        //     dueData = dueData.slice(0, 4);
        // }

        if (dueData.length > 0) {
            
            const mapData = dueData.filter((ad)=>ad.amountDue.amount!==0).map(item => (
                
                <div key={item.invoiceId} className={!((multiInvoceStreamDueAmount === 0||multiInvoceStreamDueAmount === false)) ? styles.pastDueContainer : styles.currentDueStyle}>

                    Current Due:
                    <span >
                        {moment(item.dueDate).format('MM/DD/YYYY')}
                    </span>
                    <span className={styles.currentDueAmountStyle} >
                        {getCurrentDueAmount(item.amountDue)}
                    </span>
                    {/* ${item.amountDue.amount} */}

                </div>
            ));

            return mapData.length>0?<div>{mapData}</div>:(<div className={!((multiInvoceStreamDueAmount === 0||multiInvoceStreamDueAmount === false)) ? styles.pastDueContainer : styles.currentDueStyle}>
            Current Due:
            <span>
                
            </span>
            <span className={styles.currentDueAmountStyle}>$0.00</span>
        </div>)
        }

        return (
            <div className={!((multiInvoceStreamDueAmount === 0||multiInvoceStreamDueAmount === false)) ? styles.pastDueContainer : styles.currentDueStyle}>
                
                Current Due:
                <span >
                        
                </span>
                <span className={styles.currentDueAmountStyle}>$0.00</span>
            </div>
        );
    }, [multiInvoceStreamCurrentDueArray]);

    const getAccountInvoices = useCallback(() => {
        const allMultiStreamSummaries = [];

        // eslint-disable-next-line array-callback-return
        BillingData?.allInvoiceStreamDetails.map((loopItem)=>{
            if(loopItem.internalId === multiInvoiceSelectedValue){
                loopItem.invoiceSummary.map((subLoopItem)=>{
                    allMultiStreamSummaries.push(subLoopItem);
                }) 
            }
        })

        return allMultiStreamSummaries
    }, [BillingData?.allInvoiceStreamDetails, multiInvoiceSelectedValue]);

    const getRecentPayments = useCallback(() => {        
        const selectedInvoiceData = _.find(BillingData?.allInvoiceStreamDetails, (inv) => inv.internalId === multiInvoiceSelectedValue)
        
        return _.filter(recentAccountPayments, (accountPayment) => accountPayment?.unAppliedFundId === selectedInvoiceData?.unAppliedFundId)        
    }, [BillingData?.allInvoiceStreamDetails, multiInvoiceSelectedValue, recentAccountPayments]);

    const currentDueData = useCallback(() => {
        let dueData = currentDueAmount;
        if (dueData.length > 4) {
            dueData = dueData.slice(0, 4);
        }

        if (dueData.length > 0) {
            const mapData = dueData.map(item => (
                <div key={item.id} className={!((multiInvoceStreamDueAmount === 0||multiInvoceStreamDueAmount === false)) ? styles.pastDueContainer : styles.currentDueStyle}>

                    Current Due:
                    <span >
                        {moment(item.dueDate).format('MM/DD/YYYY')}
                    </span>
                    <span className={styles.currentDueAmountStyle} >
                        {getCurrentDueAmount(item.amountDue)}
                    </span>
                    {/* ${item.amountDue.amount} */}

                </div>
            ));

            return <div>{mapData}</div>;
        }

        return (
            <div className={styles.noCurrentDueDiv}>
                Current Due:<span className={styles.noCurrentDue}>$0.00</span>
            </div>
        );
    }, [currentDueAmount]);
   
    const getYear = useCallback(() => {
        let data = paymentGroupDetails.invoiceStreamDetails.invoiceSummary.map(
            invoice => {
                const newdate = moment(invoice.dueDate).toDate();

                return {
                    code: moment(newdate).format('YYYY'),
                    name: moment(newdate).format('YYYY'),
                };
            }
        );
        
        data = data.filter(
            (value, index, self) =>
                index ===
                self.findIndex(
                    t => t.code === value.code && t.name === value.name
                )
        );
        data = data.sort((a, b) => b.code - a.code);
       
        const getCurrentYear=new Date().getFullYear();
        if(selectedyear===null && data.length>0)
        { 
            setselectedyear(getCurrentYear.toString());
            refreshData();
        }
        return data;
    }, [paymentGroupDetails.invoiceStreamDetails.invoiceSummary, refreshData, selectedyear]);

    const getDocYear = useCallback(() => {
        let data = paymentGroupDetails.billingDocuments.map(
            bd => {
                
                const newdate = moment(bd.dateModified).toDate();
                
                return {
                    code: moment(newdate).format('YYYY'),
                    name: moment(newdate).format('YYYY'),
                };
            }
        );
       
        data = data.filter(
            (value, index, self) =>
                index ===
                self.findIndex(
                    t => t.code === value.code && t.name === value.name
                )
        );
        
        data = data.sort((a, b) => b.code - a.code);
        
        const getCurrentYear=new Date().getFullYear();
        if(selectedDocYear===null && data.length>0)
        { 
            setSelectedDocYear(getCurrentYear.toString());
            refreshData();
        }
        return data;
    }, [paymentGroupDetails.billingDocuments, refreshData, selectedDocYear]);

    
    const getCurrentDue = () => {
        const currentdue = invoiceSummary.filter(res => {
            return (
                res.invoiceStatus === 'billed' && res.paidStatus === 'unpaid'
            );
        });
        setCurrentDueAmount(currentdue);
    };

    useEffect(() => {
        getCurrentDue(); 
    }, []);

    const handleYearselection = response => {
        
        if (response === undefined) {
            setselectedyear('');
        } else {
            setselectedyear(response);
        }
    };
    const handleDocYearselection = response => {
        
        if (response === undefined) {
            setSelectedDocYear(undefined);
        } else {
            setSelectedDocYear(response);
        }
    };

    const onClickCancelMakePayment = useCallback(() => {
        updateSelectedScreen();
    }, []);

    const getFormattedDate = useCallback(
        (item, index, property) => {
            return intl.formatDate(new Date(item[property.id]), {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        },
        [intl]
    );

    const onClickConfirmSetupAutomaticPayment = useCallback(
        (paymentDetailsVm, paymentMethod) => {
            const paymentDetailsValue = paymentDetailsVm.value;

            const bankAccountData = {
                ...paymentDetailsValue,
            };

            const creditCardData = {
                ...paymentDetailsValue,
            };

            let detailsValue;
            if (paymentMethod === 'wire') {
                detailsValue = { bankAccountData };
            } else {
                detailsValue = { creditCardData };
            }

            const paymentInstrumentValue = {
                paymentMethod,
                ...detailsValue,
            };

            BillingService.setPaymentGroupPaymentInformation(
                invoiceStreamId,
                paymentInstrumentValue,
                authHeader
            )
                .then(() => {
                    updateSelectedScreen('automaticConfirmation');
                })
                .catch(() => {
                    showAlert({
                        title: messages.accountUpdateFailed,
                        message:
                            messages.sorryWeWereNotAbleToSetupAutomaticPaymentsOnYourAccount,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                });
        },
        [
            BillingService,
            invoiceStreamId,
            authHeader
        ]
    );

    const onClickCancelSetupAutomaticPayment = useCallback(() => {
        updateSelectedScreen();
    }, []);

    const DownloadSummary = useCallback(() => {
        
        BillingService.createTransactionReportForAccount(
            accountNumber,
            authHeader
        ).then(res => {
            const base64 = res.base64;
            const byteString = atob(base64);

            // convert binary to array buff so we can construct a blob
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const intArray = new Uint8Array(arrayBuffer);

            for (let i = 0; i < byteString.length; i += 1) {
                intArray[i] = byteString.charCodeAt(i);
            }
            const file = new Blob([intArray], { type: res.fileType });
            const fileURL = URL.createObjectURL(file);
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.href = fileURL;
            a.download = `${res.fileName}.xlsx`;
            a.click();
        });
    }, [BillingService, accountNumber, authHeader]);

    const onClickConfirmChangePaymentSource = useCallback(
        (paymentDetailsVm, paymentMethod) => {
            const paymentInstrumentValue = createPaymentInstrumentObject(
                paymentDetailsVm,
                paymentMethod
            );

            BillingService.setPaymentGroupPaymentInformation(
                invoiceStreamId,
                paymentInstrumentValue,
                authHeader
            )
                .then(() => {
                    updateSelectedScreen('paymentSourceConfirmation');
                })
                .catch(() => {
                    showAlert({
                        title: messages.changingPaymentMethod,
                        message:
                            messages.thereWasAProblemChangingThePaymentMethod,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                });
        },
        [
            BillingService,
            invoiceStreamId,
            authHeader
        ]
    );

    const onClickCancelChangePaymentSource = useCallback(() => {
        updateSelectedScreen();
    }, []);

    const onUpdateSelectedInvoices = useCallback(
        selectedInvoiceSummariesId => {
            const selectedInvoiceObjects = _.filter(
                invoiceSummary,
                anInvoiceSummary => {
                    return selectedInvoiceSummariesId.includes(
                        anInvoiceSummary.invoiceId
                    );
                }
            );

            const selectedTotalAmount = _.sumBy(
                selectedInvoiceObjects,
                invoice => {
                    return Number(_.get(invoice, 'amountDue.amount'));
                }
            ).toFixed(2);

            updateSelectedInvoiceAmount(selectedTotalAmount);
            const amountObject = {
                amount: selectedTotalAmount,
                currency: currency,
            };

            updateManualAmount(amountObject);
            updateSelectedInvoicesForPayment(selectedInvoiceSummariesId);
        },
        [invoiceSummary, currency]
    );

    const onClickMenuLink = useCallback(
        evt => {
            setTabValue(false);
            const { id: menuLinkId } = evt.currentTarget;
            let screen;
            switch (menuLinkId) {
                case 'makeAPaymentLinkId':
                    screen = 'payment';
                    break;
                case 'setupAutomaticPaymentsLinkId':
                    screen = 'automatic';
                    break;
                case 'changePaymentMethodLinkId':
                    screen = 'payment-source';
                    break;
                case 'viewPoliciesLinkId':
                    screen = 'policy-list';
                    break;
                case 'viewInvoicesLinkId':
                case 'default':
                    screen = 'default';
                    break;
                default:
                    screen = 'default';
                    resetInternalStates();
                    break;
            }
            updateSelectedScreen(screen);
        },
        []
    );

    const onClickFilterChange = useCallback(value => {
        updateSelectedInvoiceFilter(value);
    }, []);

    const paymentPage = useCallback(() => {
        const { history } = props;
        trackPageView('Make a Payment', '/make-payment');
        window.sessionStorage.setItem('make-payment',JSON.stringify({
            pathname: `/make-payment`,
            state: {
                redirectPath: '/billing-summary',
                dueAmount: paymentGroupDetails.totalOutstandingAmountDue.amount,
                accountNumber: accountNumber,
                multiInvoiceStreamData:multiInvoiceStreamData,
                multiInvoiceSelectedValue:multiInvoiceSelectedValue,
                accountsData:accountsData
            }
        }));
        return history.push({
            pathname: `/make-payment`,
            state: {
                redirectPath: '/billing-summary',
                dueAmount: paymentGroupDetails.totalOutstandingAmountDue.amount,
                accountNumber: accountNumber,
                multiInvoiceStreamData:multiInvoiceStreamData,
                multiInvoiceSelectedValue:multiInvoiceSelectedValue,
                accountsData:accountsData
            },
        });
    }, [accountNumber, paymentGroupDetails]);

    const isAmountToPayValid = useCallback(
        amount => {
            return (
                selectedInvoiceAmount < amount ||
                amount === 0 ||
                _.isNull(amount)
            );
        },
        [selectedInvoiceAmount]
    );

    // eslint-disable-next-line no-shadow
    const getEnrollValue = useCallback(paymentGroupDetails => {
        if (
            paymentGroupDetails.basicDetails.enrolledForAutomticPayment ===
            false
        ) {
            return <FieldValue value={messages.notEnrolled.defaultMessage} />;
        }

        return <FieldValue value={messages.Enrolled.defaultMessage} />;
    }, []);

    const getEnrollData = useCallback(() => {
        let automaticPaymentAccountNum ='';
        paymentGroupDetails.allInvoiceStreamDetails.map((loopI)=>{
            if(loopI.internalId === multiInvoiceSelectedValue){
                // automaticPaymentAccountNum=loopI.bankAccountNumber;
                if(loopI.enrolledForAutomticPayment ===true){
                    paymentGroupDetails.bankAccountNumber =loopI.bankAccountNumber;
                    paymentGroupDetails.basicDetails.enrolledForAutomticPayment = loopI.enrolledForAutomticPayment;
                }else{
                    delete paymentGroupDetails.bankAccountNumber
                    paymentGroupDetails.basicDetails.enrolledForAutomticPayment = false; 
                }
            }
        })
        if (paymentGroupDetails.hasOwnProperty('bankAccountNumber')) {
            let EnrollValue = ' ';

            if (
                paymentGroupDetails.basicDetails.enrolledForAutomticPayment ===
                false
            ) {
                EnrollValue = 'Not Enrolled';
            } else {
                EnrollValue = 'Enrolled ';
            }
            /* <span>Account{paymentGroupDetails.bankAccountNumber}</span>*/
            return (
                <div >
                <p className={styles.BillingAccountEnroll}>Automatic Payment:</p>
                <span className={styles.BillingAccountEnrollContent}>{EnrollValue}</span>
                <span className={styles.BillingAccountEnrollContent}>{` Bank Account ${MaskCharacter(
                    paymentGroupDetails.bankAccountNumber,
                    'x',
                    4
                )}`}</span>


            </div>
            );
        }
        let EnrollValue = ' ';

        if (
            paymentGroupDetails.basicDetails.enrolledForAutomticPayment ===
            false
        ) {
            EnrollValue = 'Not Enrolled';
        } else {
            EnrollValue = 'Enrolled';
        }
        
        return (
            <div >
                <p className={styles.BillingAccountEnroll}>Automatic Payment:</p>
                <span className={styles.BillingAccountEnrollContent}>{EnrollValue}</span>
            </div>
        );
         
    }, [multiInvoiceSelectedValue, paymentGroupDetails]);

    function MaskCharacter(str, mask, n = 1) {
        // Slice the string and replace with
        // mask then add remaining string
        return [...str].reduce(
            (acc, x, i) => (i < str.length - n ? acc + mask : acc + x),
            ''
        );
    }

    const getStatus = useCallback(
        isDelinquent => {
            if (isDelinquent) {
                return translator(messages.delinquentLabel);
            }
            return translator(messages.inGoodStandingLabel);
        },
        []
    );

    const getBillingStatus = useCallback(() => {
        const BillingAccount = accountNumber;
        const BillingStatus = getStatus(
            paymentGroupDetails.basicDetails.isDelinquent
        );
        return (
            <div className={styles.BillingAccountCss}>
                <div>Billing Account: {BillingAccount}</div>
                <div className = {styles.statusClass}>Status: {BillingStatus}</div>
            </div>
        );
    }, [accountNumber, paymentGroupDetails]);

    // eslint-disable-next-line no-shadow
    // const getStatus=useCallback((paymentGroupDetails) => {
    //     // if (paymentGroupDetails.basicDetails.enrolledForAutomticPayment===false) {
    //     //     return <FieldValue  value={messages.notEnrolled.defaultMessage} />;
    //     // }

    //     // return <FieldValue  value={messages.Enrolled.defaultMessage} />;
    //     return (
    //         `Status:${}`
    //     );
    // },[]);

    useEffect(() => {
        const defaultFilter = hasUnpaidInvoices(invoiceSummary)
            ? 'planned'
            : 'paid';
        updateSelectedInvoiceFilter(defaultFilter);
    }, [invoiceSummary]);

    const cancelAutomaticPayment = useCallback(() => {
        const { history } = props;

        
        return history.push({
            pathname: `/automatic-payment`,
            state: {
                accountNumber: accountNumber,
                data: paymentGroupDetails,
                page: 'cancelpayment',
                type: 'cancel',
                multiInvoiceStreamData:multiInvoiceStreamData,
                multiInvoiceSelectedValue:history?.location?.state?.multiInvoiceSelectedValue !== undefined ? history?.location?.state?.multiInvoiceSelectedValue : multiInvoiceSelectedValue
            },
        });
    }, [accountNumber, paymentGroupDetails]);

    const setUpAutomaticPayment = useCallback(() => {
        const { history } = props;

        if (paymentGroupDetails.basicDetails.enrolledForAutomticPayment) {
            return history.push({
                pathname: `/automatic-payment`,
                state: {
                    accountNumber: accountNumber,
                    data: paymentGroupDetails,
                    page: 'automaticsetup',
                    type: 'change',
                    multiInvoiceStreamData:multiInvoiceStreamData,
                    multiInvoiceSelectedValue:history?.location?.state?.multiInvoiceSelectedValue !== undefined ? history?.location?.state?.multiInvoiceSelectedValue : multiInvoiceSelectedValue
                },
            });
        }
        return history.push({
            pathname: `/automatic-payment`,
            state: {
                accountNumber: accountNumber,
                data: paymentGroupDetails,
                page: 'automaticsetup',
                type: 'setup',
                multiInvoiceStreamData:multiInvoiceStreamData,
                multiInvoiceSelectedValue:history?.location?.state?.multiInvoiceSelectedValue !== undefined ? history?.location?.state?.multiInvoiceSelectedValue : multiInvoiceSelectedValue
            },
        });
    }, [accountNumber, paymentGroupDetails]);

    const displayNotification = () => {
        if (window.location.href.indexOf('refId') > -1) return true;
        return false;
    };

    const getPaymentInfoMessage = () => {
        if (!_.isEmpty(window.location.search)) {
            const queryParams = new URLSearchParams(window.location.search);
            const referenceId = queryParams.get('refId');
            const infomesg = translator(messages.paymentSuccessMessage,{referenceId:referenceId})
            return (
                <InlineNotification
                    id="inline-notification"
                    className="paymentInfoNotifMesg"
                   message={infomesg}
                    type="info"
                    isDismissable={false}
                />
            );
        }
    };

    if (isLoadingData) {
        return <LoaderExt loaded={!isLoadingData} />;
    }
    
    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            phoneWide: {
                labelPosition: 'top',
            },
        },

        billingDetailsContainer: {
            visible: tabValue,
        },
        billingPagePolicyBillingStatus: {
            value: getStatus(paymentGroupDetails.basicDetails.isDelinquent),
        },
        billingAccountNumber: {
            value: accountNumber,
        },
        currentDueId: {
            content: intl.formatDate(
                new Date(paymentGroupDetails.basicDetails.currentDueDate),
                { year: 'numeric', month: 'short', day: 'numeric' }
            ),
        },
        amountDueId: {
            content: `$${paymentGroupDetails.basicDetails.currentDue.amount}`,
        },
        automaticPaymentsEnrollid: {
            // value: getEnrollValue(paymentGroupDetails),
            content: getEnrollData(),
        },

        menuLinkContainerId: {
            visible: tabValue,
        },
        pastDueAmount: {
            value: (multiInvoiceSelectedValue !=='' || multiInvoiceSelectedValue !==undefined)?multiInvoceStreamDueAmount:paymentGroupDetails.pastAmountDue,
            visible: (multiInvoceStreamDueAmount !== 0 &&  multiInvoiceSelectedValue !==undefined) || multiInvoceStreamDueAmount !== false &&  multiInvoiceSelectedValue !==undefined ,
        },
        YearFilterDropDown: {
            availableValues: getYear(paymentGroupDetails),
            value: selectedyear,
            onValueChange: handleYearselection,
        },
        YearFilterDocDropDown:{
            availableValues: getDocYear(paymentGroupDetails),
            value: selectedDocYear,
            onValueChange: handleDocYearselection,
        },
        bilingDocumentsId: {
            visible: true,
        },

        makeAPaymentLinkId: {
            visible: true,
        },
        viewPoliciesLinkId: {
            visible: true,
        },
        changePaymentMethodLinkId: {
            visible: true,
        },
        billingInvoicesContainerId: {
            visible: true,
        },
        billingInvoicesHeaderId: {
            content: getInvoiceStreamHeader(
                accountLevelBilling,
                paymentGroupDetails,
                lobCode
            ),
            visible: invoiceStreamId || accountLevelBilling,
        },
        invoicesFilterToggleId: {
            onValueChange: onClickFilterChange,
        },
        billingInvoicesComponentId: {
            invoiceSummary:getAccountInvoices(),//invoiceSummary,

            selectedyear: selectedyear,
        },

        billingRecentAccountsId: {
            invoiceSummary: getRecentPayments(),
            
        },
        currentDueData: {
            content: (multiInvoiceSelectedValue !=='' || multiInvoiceSelectedValue !==undefined)?currentDueDataMultiInvoiceStream(): currentDueData(),
        },
        BillingDocumentsTableId: {
            invoiceSummary: billingDocuments,
            selectedDocYear:selectedDocYear
        },
        billingInvoicesPaidComponentId: {
            invoiceSummary: getAnyPaidInvoices(invoiceSummary),

            showPaidDate: selectedInvoiceFilter === 'paid',
        },
        makePaymentContainerId: {
            visible: selectedScreen === 'payment',
        },
        makePaymentInvoicesTableComponentId: {
            invoiceSummary: getUnpaidInvoices(invoiceSummary),
            selectedInvoices: selectedInvoicesForPayment,
            onUpdateSelectedInvoices: onUpdateSelectedInvoices,
        },
        BillingaccountStatusId: {
            content: getBillingStatus(),
        },
        makePaymentSourceComponentId: {
            title: messages.setPaymentSource,
            xCenter: 'bc',
            model: paymentInstrument,
            isDisabled: selectedInvoicesForPayment.length === 0,
            isSelectedInvoiceAmount:
                manualAmountToPay &&
                isAmountToPayValid(manualAmountToPay.amount),
        },
        makePaymentAmountToPayId: {
            defaultCurrency: currency,
            disabled: selectedInvoicesForPayment.length === 0,
            onValueChange: updateManualAmount,
        },
        makePaymentConfirmationContainerId: {
            visible: selectedScreen === 'paymentConfirmation',
        },
        makePaymentConfirmationMessageId: {
            content: translator(messages.paymentOfHasBeenAppliedToYourAccount, {
                paidAmount: intl.formatNumber(payingAmountValue, {
                    style: 'currency',
                    currency: currency,
                    currencyDisplay: 'code',
                }),
            }),
        },
        setUpAutomaticPaymentContainerId: {
            visible: selectedScreen === 'automatic',
        },
        setupAutomaticPaymentComponentId: {
            xCenter: 'bc',
            model: paymentInstrument,
        },
        setUpAutomaticPaymentConfirmationContainerId: {
            visible: selectedScreen === 'automaticConfirmation',
        },
        pastDueContainer: {
            visible: !((multiInvoceStreamDueAmount === 0||multiInvoceStreamDueAmount === false)),
        },
        setUpAutomaticPaymentConfirmationInfoId: {
            content: translator(messages.yourAutomaticPaymentsWillBeginOn, {
                startingDate: intl.formatDate(new Date(firstInvoiceDueDate), {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                }),
            }),
        },
        changePaymentSourceContainerId: {
            visible: selectedScreen === 'payment-source',
        },
        changePaymentSourceFormComponentId: {
            title: messages.changePaymentMethod,
            xCenter: 'bc',
            model: paymentInstrument,
        },
        changePaymentSourceConfirmationContainerId: {
            visible: selectedScreen === 'paymentSourceConfirmation',
        },
        policyListContainerId: {
            visible: selectedScreen === 'policy-list',
        },
        viewPoliciesComponentId: {
            policySummaries: policySummaries,
            policyNumbers: relatedPolicies,
        },
        cancelAutomaticPaymentsLinkId: {
            visible:
                paymentGroupDetails.basicDetails.enrolledForAutomticPayment,
        },
        setupAutomaticPaymentsLinkId: {
            visible: !paymentGroupDetails.basicDetails
                .enrolledForAutomticPayment,
        },
        ChangeBankAccountLinkId: {
            visible:
                paymentGroupDetails.basicDetails.enrolledForAutomticPayment,
        },
        paymentsuccessinfo: {
            visible: displayNotification(),
            content: getPaymentInfoMessage(),
        },
        // DocumentsYears:{
        //     className:"filtersubtitleNew"
        // }
        // tableContainerYear:{
        //     className:"tableContainerNewClass" 
        // }
    };

    const dataForComponent = {
        invoiceStreamId,
        selectedInvoiceFilter,
        periodicity,
        startingDate: firstInvoiceDueDate,
        manualAmountToPay,
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            paymentcomponent: PaymentComponent,
            RecentAccountPaymentTable: RecentAccountPaymentTable,
            BillingDocumentsTable: BillingDocumentsTable,
        },
        resolveCallbackMap: {
            onClickMenuLink,
            // onClickConfirmMakePayment,
            onClickCancelMakePayment,
            onClickConfirmSetupAutomaticPayment,
            onClickCancelSetupAutomaticPayment,
            onClickConfirmChangePaymentSource,
            onClickCancelChangePaymentSource,
            getFormattedDate,
            getStatus,
            handleYearselection,
            DownloadSummary,
            paymentPage,
            getCurrentDue,
            currentDueData,
            getBillingStatus,
            cancelAutomaticPayment,
            setUpAutomaticPayment,
        },
    };

    return (
        <MetadataForm
            data={dataForComponent}
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            isUsingNewValidation />
    );
}

BillingSummary.propTypes = {
    invoiceStreamId: PropTypes.string,
    accountLevelBilling: PropTypes.bool,
    lobCode: PropTypes.string,
    paymentGroupDetails: PropTypes.shape({
        invoiceSummary: PropTypes.array,
        relatedPolicies: PropTypes.array,
        paymentInstrument: PropTypes.object,
        paymentInstrumentSummary: PropTypes.object,
        periodicity: PropTypes.string,
    }),
    currency: PropTypes.string.isRequired,
    policySummaries: PropTypes.arrayOf(PropTypes.object),
    authHeader: PropTypes.shape({}).isRequired,
    reloadInvoiceDetails: PropTypes.func.isRequired,
};

BillingSummary.defaultProps = {
    invoiceStreamId: null,
    accountLevelBilling: null,
    lobCode: null,
    paymentGroupDetails: {
        invoiceSummary: [],
        relatedPolicies: [],
        paymentInstrument: null,
        paymentInstrumentSummary: null,
        periodicity: null,
    },
    policySummaries: [],
};

export default withAuthenticationContext(BillingSummary);
